import { useId, useMemo, useRef, useState, type ReactNode } from 'react'
import { ArrowLeft, ArrowRight, Check, Lock, Paperclip, Sigma } from 'lucide-react'
import {
  FILE_FORMATS,
  READONLY_SOURCES,
  evaluateCondition,
  validateAnswers,
  type AnswerValidation,
  type Answers,
  type FieldOption,
  type FormField,
  type FormSchema,
  type ReadonlySource,
} from '@concours/shared'
import { Button } from '../components/Button'
import { Input, Select, Textarea } from '../components/Field'
import { cx } from '../lib/cx'

export interface FormRendererProps {
  schema: FormSchema
  value: Answers
  onChange: (next: Answers) => void
  /** Valeurs système des champs en lecture seule (matricule, référence…). */
  readonlyValues?: Partial<Record<ReadonlySource, string>>
  /** Erreurs renvoyées par l'API (elles priment : c'est le serveur qui décide). */
  serverErrors?: Record<string, string>
  /** Dernière étape validée côté client → résultat complet (mode « soumission »). */
  onComplete?: (result: AnswerValidation) => void
  completeLabel?: string
  /** `steps` : une section par étape. `single` : tout sur une page. */
  layout?: 'steps' | 'single'
  /** Pièces jointes : en aperçu, le fichier est choisi mais jamais envoyé. */
  onFile?: (field: FormField, file: File) => Promise<{ fileId: string; name: string }>
  /** Pièces jointes indisponibles : message affiché à la place du sélecteur de fichier. */
  filesUnavailable?: string
  /**
   * Champ « pièce » rendu par le portail (dépôt sécurisé, analyse, aperçu).
   * Prioritaire sur `onFile` : la pièce est gérée hors des réponses saisies.
   */
  renderFile?: (props: FileFieldRenderProps) => ReactNode
  disabled?: boolean
}

/** Ce que le portail reçoit pour rendre un champ « pièce ». */
export interface FileFieldRenderProps {
  field: FormField
  id: string
  hint?: string
  error?: string
}

const today = () => new Date().toISOString().slice(0, 10)

/**
 * Rendu dynamique d'un formulaire de candidature à partir de sa définition
 * (TODO phase 7) — même moteur que l'API : conditions, calculs et validation
 * de `@concours/shared`. Le portail guide ; l'API revalide tout au dépôt.
 */
export function FormRenderer({
  schema,
  value,
  onChange,
  readonlyValues,
  serverErrors = {},
  onComplete,
  completeLabel = 'Vérifier le formulaire',
  layout = 'steps',
  onFile,
  filesUnavailable,
  renderFile,
  disabled,
}: FormRendererProps) {
  const [step, setStep] = useState(0)
  const [shown, setShown] = useState<Record<string, string>>({})
  const top = useRef<HTMLDivElement>(null)

  // État courant (mode brouillon) : visibilité, calculs, champs restants.
  const draft = useMemo(
    () => validateAnswers(schema, value, { mode: 'draft', today: today(), readonlyValues }),
    [schema, value, readonlyValues]
  )
  const visibleSections = schema.sections.filter((s) =>
    evaluateCondition(s.visibility, draft.values)
  )
  const current =
    layout === 'steps' ? visibleSections[Math.min(step, visibleSections.length - 1)] : undefined
  const sections = layout === 'steps' ? (current ? [current] : []) : visibleSections
  const isLast = layout === 'single' || step >= visibleSections.length - 1

  const set = (key: string, v: unknown) => {
    const next = { ...value }
    if (v === undefined || v === '' || (Array.isArray(v) && !v.length)) delete next[key]
    else next[key] = v
    onChange(next)
    if (shown[key])
      setShown((prev) => Object.fromEntries(Object.entries(prev).filter(([k]) => k !== key)))
  }

  /** Contrôle des champs visibles des sections affichées (mode soumission). */
  const check = () => {
    const full = validateAnswers(schema, value, { mode: 'submit', today: today(), readonlyValues })
    const keys = new Set(sections.flatMap((s) => s.fields.map((f) => f.key)))
    const errors = Object.fromEntries(
      Object.entries(full.errors).filter(([k]) => keys.has(k) || k === '_form')
    )
    setShown(errors)
    const first = Object.keys(errors)[0]
    if (first) document.getElementById(`field-${first}`)?.focus()
    return { full, ok: !Object.keys(errors).length }
  }

  const next = () => {
    const { full, ok } = check()
    if (!ok) return
    if (isLast) onComplete?.(full)
    else {
      setStep((s) => s + 1)
      top.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' })
    }
  }

  const errorFor = (key: string) => serverErrors[key] ?? shown[key]

  return (
    <div className="form-renderer" ref={top}>
      {layout === 'steps' && visibleSections.length > 1 && (
        <ol className="wizard-steps" aria-label="Étapes du formulaire">
          {visibleSections.map((s, i) => (
            <li key={s.key}>
              <button
                type="button"
                aria-current={i === step ? 'step' : undefined}
                // On ne saute pas en avant sans contrôler l'étape en cours.
                onClick={() => (i < step ? setStep(i) : i > step ? next() : undefined)}
              >
                {s.title}
              </button>
            </li>
          ))}
        </ol>
      )}
      <p className="form-remaining" aria-live="polite">
        {draft.missing.length
          ? `${draft.missing.length} champ${draft.missing.length > 1 ? 's' : ''} obligatoire${draft.missing.length > 1 ? 's' : ''} restant${draft.missing.length > 1 ? 's' : ''}`
          : 'Tous les champs obligatoires sont renseignés.'}
      </p>
      {serverErrors._form && (
        <p className="field-error" role="alert">
          {serverErrors._form}
        </p>
      )}

      <fieldset
        disabled={disabled}
        className="stack"
        style={{ border: 0, padding: 0, margin: 0, gap: 'var(--space-6)' }}
      >
        {sections.map((section) => (
          <section
            key={section.key}
            className="form-section"
            aria-labelledby={`section-${section.key}`}
          >
            <h3 id={`section-${section.key}`} className="form-section-title">
              {section.title}
            </h3>
            {section.description && (
              <p className="text-muted form-section-desc">{section.description}</p>
            )}
            <div className="stack" style={{ gap: 'var(--space-4)' }}>
              {section.fields
                .filter((f) => draft.visible.includes(f.key))
                .map((field) => (
                  <FieldRenderer
                    key={field.key}
                    field={field}
                    value={
                      field.type === 'computed' || field.type === 'readonly'
                        ? draft.values[field.key]
                        : value[field.key]
                    }
                    onChange={(v) => set(field.key, v)}
                    error={errorFor(field.key)}
                    onFile={onFile}
                    filesUnavailable={filesUnavailable}
                    renderFile={renderFile}
                  />
                ))}
            </div>
          </section>
        ))}
      </fieldset>

      <div
        className="row"
        style={{ justifyContent: 'space-between', marginBlockStart: 'var(--space-5)' }}
      >
        {layout === 'steps' ? (
          <Button
            variant="secondary"
            icon={<ArrowLeft />}
            disabled={step === 0}
            onClick={() => {
              setShown({})
              setStep((s) => Math.max(0, s - 1))
            }}
          >
            Précédent
          </Button>
        ) : (
          <span />
        )}
        <Button
          variant="primary"
          iconEnd={isLast ? <Check /> : <ArrowRight />}
          onClick={next}
          disabled={disabled}
        >
          {isLast ? completeLabel : 'Suivant'}
        </Button>
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Champs                                                              */
/* ------------------------------------------------------------------ */

interface FieldRendererProps {
  field: FormField
  value: unknown
  onChange: (value: unknown) => void
  error?: string
  onFile?: FormRendererProps['onFile']
  filesUnavailable?: string
  renderFile?: FormRendererProps['renderFile']
}

function FieldRenderer({
  field,
  value,
  onChange,
  error,
  onFile,
  filesUnavailable,
  renderFile,
}: FieldRendererProps) {
  const id = `field-${field.key}`
  const hint = [field.description, field.help].filter(Boolean).join(' — ') || undefined
  const common = { id, label: field.label, required: field.required, hint, error }
  const v = field.validation ?? {}

  switch (field.type) {
    case 'text':
    case 'email':
    case 'phone':
      return (
        <Input
          {...common}
          type={field.type === 'email' ? 'email' : field.type === 'phone' ? 'tel' : 'text'}
          autoComplete={field.type === 'email' ? 'email' : field.type === 'phone' ? 'tel' : 'off'}
          maxLength={v.maxLength}
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.target.value)}
        />
      )
    case 'textarea':
      return (
        <Textarea
          {...common}
          rows={4}
          maxLength={v.maxLength}
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.target.value)}
        />
      )
    case 'integer':
    case 'decimal':
      return (
        <Input
          {...common}
          type="number"
          inputMode={field.type === 'integer' ? 'numeric' : 'decimal'}
          step={field.type === 'integer' ? 1 : 'any'}
          min={v.min}
          max={v.max}
          value={typeof value === 'number' ? value : ''}
          hint={[hint, field.unit].filter(Boolean).join(' · ') || undefined}
          onChange={(e) => onChange(e.target.value === '' ? undefined : Number(e.target.value))}
        />
      )
    case 'date':
      return (
        <Input
          {...common}
          type="date"
          min={v.minDate === 'today' ? today() : v.minDate}
          max={v.maxDate === 'today' ? today() : v.maxDate}
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.target.value || undefined)}
        />
      )
    case 'select':
      return (
        <Select
          {...common}
          placeholder="— Choisir —"
          value={typeof value === 'string' ? value : ''}
          options={field.options ?? []}
          onChange={(e) => onChange(e.target.value || undefined)}
        />
      )
    case 'radio':
    case 'multiselect':
      return (
        <ChoiceGroup
          field={field}
          id={id}
          hint={hint}
          error={error}
          multiple={field.type === 'multiselect'}
          value={value}
          onChange={onChange}
        />
      )
    case 'checkbox':
      return (
        <div className="field">
          <label className="check-row" htmlFor={id}>
            <input
              id={id}
              type="checkbox"
              checked={value === true}
              aria-invalid={Boolean(error) || undefined}
              aria-describedby={error ? `${id}-error` : undefined}
              onChange={(e) => onChange(e.target.checked ? true : undefined)}
            />
            <span>
              {field.label}
              {field.required && (
                <span className="req" aria-hidden="true">
                  *
                </span>
              )}
              {hint && (
                <span className="field-hint" style={{ display: 'block' }}>
                  {hint}
                </span>
              )}
            </span>
          </label>
          {error && (
            <p className="field-error" id={`${id}-error`} role="alert">
              {error}
            </p>
          )}
        </div>
      )
    case 'file':
    case 'image':
      if (renderFile) return <>{renderFile({ field, id, hint, error })}</>
      return (
        <FileField
          field={field}
          id={id}
          hint={hint}
          error={error}
          value={value}
          onChange={onChange}
          onFile={onFile}
          unavailable={filesUnavailable}
        />
      )
    case 'computed':
      return (
        <SystemValue
          id={id}
          label={field.label}
          hint={hint}
          icon={<Sigma aria-hidden="true" size={14} />}
        >
          {value === null || value === undefined ? (
            <span className="text-muted">Calculé automatiquement</span>
          ) : (
            `${String(value)}${field.unit ? ` ${field.unit}` : ''}`
          )}
        </SystemValue>
      )
    case 'readonly':
      return (
        <SystemValue
          id={id}
          label={field.label}
          hint={hint}
          icon={<Lock aria-hidden="true" size={14} />}
        >
          {typeof value === 'string' && value ? (
            <span className="mono">{value}</span>
          ) : (
            <span className="text-muted">
              {field.source ? `${READONLY_SOURCES[field.source]} — attribué automatiquement` : '—'}
            </span>
          )}
        </SystemValue>
      )
    default:
      return null
  }
}

function SystemValue({
  id,
  label,
  hint,
  icon,
  children,
}: {
  id: string
  label: string
  hint?: string
  icon: ReactNode
  children: ReactNode
}) {
  return (
    <div className="field">
      <span className="field-label" id={`${id}-label`}>
        {label}
      </span>
      <output className="system-value" id={id} aria-labelledby={`${id}-label`} tabIndex={-1}>
        {icon}
        {children}
      </output>
      {hint && <p className="field-hint">{hint}</p>}
    </div>
  )
}

function ChoiceGroup({
  field,
  id,
  hint,
  error,
  multiple,
  value,
  onChange,
}: {
  field: FormField
  id: string
  hint?: string
  error?: string
  multiple: boolean
  value: unknown
  onChange: (value: unknown) => void
}) {
  const name = useId()
  const selected = multiple ? (Array.isArray(value) ? (value as string[]) : []) : [value as string]
  const toggle = (option: FieldOption, checked: boolean) => {
    if (!multiple) return onChange(option.value)
    const next = checked ? [...selected, option.value] : selected.filter((v) => v !== option.value)
    onChange(field.options?.map((o) => o.value).filter((v) => next.includes(v)))
  }
  return (
    <fieldset
      className={cx('field', 'choice-group')}
      aria-describedby={error ? `${id}-error` : hint ? `${id}-hint` : undefined}
      aria-invalid={Boolean(error) || undefined}
    >
      <legend className="field-label">
        {field.label}
        {field.required && (
          <span className="req" aria-hidden="true">
            *
          </span>
        )}
      </legend>
      <div className="choice-options">
        {field.options?.map((option, i) => (
          <label key={option.value} className="check-row">
            <input
              id={i === 0 ? id : undefined}
              type={multiple ? 'checkbox' : 'radio'}
              name={name}
              value={option.value}
              checked={selected.includes(option.value)}
              onChange={(e) => toggle(option, e.target.checked)}
            />
            <span>{option.label}</span>
          </label>
        ))}
      </div>
      {hint && !error && (
        <p className="field-hint" id={`${id}-hint`}>
          {hint}
        </p>
      )}
      {error && (
        <p className="field-error" id={`${id}-error`} role="alert">
          {error}
        </p>
      )}
    </fieldset>
  )
}

function FileField({
  field,
  id,
  hint,
  error,
  value,
  onChange,
  onFile,
  unavailable,
}: {
  field: FormField
  id: string
  hint?: string
  error?: string
  value: unknown
  onChange: (value: unknown) => void
  onFile?: FormRendererProps['onFile']
  unavailable?: string
}) {
  const [problem, setProblem] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const formats = field.file?.formats ?? []
  const accept = formats.map((f) => FILE_FORMATS[f].mime).join(',')
  const max = field.file?.maxSizeMb ?? 5
  const current = value && typeof value === 'object' ? (value as { name?: string }) : null
  const rules = `${formats.map((f) => FILE_FORMATS[f].label).join(', ')} · ${max} Mo maximum`

  return (
    <div className="field">
      <label className="field-label" htmlFor={id}>
        {field.label}
        {field.required && (
          <span className="req" aria-hidden="true">
            *
          </span>
        )}
      </label>
      {unavailable ? (
        <p className="file-drop text-muted" id={id} role="note">
          <Paperclip aria-hidden="true" size={16} /> {unavailable}
        </p>
      ) : (
        <div className="file-drop">
          <Paperclip aria-hidden="true" size={16} />
          <span className="grow">{current?.name ?? 'Aucun fichier choisi'}</span>
          <input
            id={id}
            type="file"
            accept={accept}
            disabled={busy}
            aria-describedby={`${id}-rules`}
            aria-invalid={Boolean(error || problem) || undefined}
            onChange={async (e) => {
              const file = e.target.files?.[0]
              e.target.value = ''
              if (!file) return
              // Contrôle indicatif ; le serveur revérifie type réel et taille (phase 10).
              if (!formats.some((f) => FILE_FORMATS[f].mime === file.type))
                return setProblem(`Format refusé. Attendu : ${rules}.`)
              if (file.size > max * 1024 * 1024)
                return setProblem(`Fichier trop lourd (${max} Mo maximum).`)
              setProblem(null)
              if (!onFile) return onChange({ fileId: `apercu-${Date.now()}`, name: file.name })
              setBusy(true)
              try {
                onChange(await onFile(field, file))
              } catch {
                setProblem('Le fichier n’a pas pu être envoyé. Réessayez.')
              } finally {
                setBusy(false)
              }
            }}
          />
        </div>
      )}
      <p className="field-hint" id={`${id}-rules`}>
        {[hint, rules].filter(Boolean).join(' — ')}
      </p>
      {(error || problem) && (
        <p className="field-error" role="alert">
          {problem ?? error}
        </p>
      )}
    </div>
  )
}
